import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { QueueItems, EmptyState, BufferLoading } from '~/shared-components';
import { Button } from '@bufferapp/ui';
import ComposerPopover from '~/composer-popover';
import getErrorBoundary from '~/web/components/ErrorBoundary';
import ProfilesDisconnectedBanner from '~/profiles-disconnected-banner';

const ErrorBoundary = getErrorBoundary(true);

const loadingContainerStyle = {
  width: '100%',
  height: '100%',
  textAlign: 'center',
  paddingTop: '5rem',
};

const topBarContainerStyle = {
  display: 'flex',
};

const composerStyle = {
  flexGrow: '1',
};

const loadMoreButtonStyle = {
  textAlign: 'center',
  paddingBottom: '3rem',
};

const NoPostsPublished = ({ total, has30DaySentPostsLimitFeature }) => {
  if (typeof total === 'undefined' || total > 0) {
    return null;
  }
  return (
    <EmptyState
      height="initial"
      title={
        has30DaySentPostsLimitFeature
          ? 'You haven’t published any posts with this account in the past 30 days!'
          : 'You haven’t published any posts with this account!'
      }
      subtitle="Once a post has gone live via Buffer, you can track its performance here to learn what works best with your audience!"
      heroImg="https://s3.amazonaws.com/buffer-publish/images/empty-sent2x.png"
      heroImgSize={{ width: '270px', height: '150px' }}
    />
  );
};

NoPostsPublished.propTypes = {
  total: PropTypes.number,
  has30DaySentPostsLimitFeature: PropTypes.bool,
};

NoPostsPublished.defaultProps = {
  total: undefined,
  has30DaySentPostsLimitFeature: true,
};

const SentPosts = ({
  total,
  loading,
  items,
  onEditClick,
  onShareAgainClick,
  onShareLinkClick,
  onCampaignTagClick,
  onComposerCreateSuccess,
  showComposer,
  editMode,
  isManager,
  isDisconnectedProfile,
  has30DaySentPostsLimitFeature,
  hasFirstCommentFlip,
  hasCampaignsFeature,
  moreToLoad,
  tabId,
  profileId,
  page,
  loadMore,
  analyzeCrossSale,
  fetchSentPosts,
  profileServiceType,
  profileService,
  hasAnalyticsOnPosts,
  hasTwitterImpressions,
  onConfirmDismissComposer,
}) => {
  useEffect(() => {
    fetchSentPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  if (loading) {
    return (
      <div style={loadingContainerStyle}>
        <BufferLoading size={64} />
      </div>
    );
  }

  const loadMorePosts = () => {
    loadMore({ profileId, page, tabId });
  };

  return (
    <ErrorBoundary>
      {isDisconnectedProfile && <ProfilesDisconnectedBanner />}
      <NoPostsPublished
        has30DaySentPostsLimitFeature={has30DaySentPostsLimitFeature}
        total={total}
      />

      {total > 0 ? (
        <>
          <div>
            <div style={topBarContainerStyle}>
              {showComposer && !editMode && (
                <div style={composerStyle}>
                  <ComposerPopover
                    onSave={onComposerCreateSuccess}
                    type="sent"
                    onConfirmDismissComposer={onConfirmDismissComposer}
                  />
                </div>
              )}
            </div>
            {showComposer && editMode && (
              <ComposerPopover
                onSave={onComposerCreateSuccess}
                type="sent"
                onConfirmDismissComposer={onConfirmDismissComposer}
              />
            )}
            <QueueItems
              items={items}
              onEditClick={onEditClick}
              onShareAgainClick={onShareAgainClick}
              onShareLinkClick={onShareLinkClick}
              onCampaignTagClick={onCampaignTagClick}
              isManager={isManager}
              hasFirstCommentFlip={hasFirstCommentFlip}
              hasCampaignsFeature={hasCampaignsFeature}
              analyzeCrossSale={analyzeCrossSale}
              profileServiceType={profileServiceType}
              profileService={profileService}
              hasAnalyticsOnPosts={hasAnalyticsOnPosts}
              hasTwitterImpressions={hasTwitterImpressions}
              shouldHideCreatePostButton
            />
          </div>
          {moreToLoad && (
            <div style={loadMoreButtonStyle}>
              <Button
                type="primary"
                label="Load More"
                onClick={loadMorePosts}
              />
            </div>
          )}
        </>
      ) : null}
    </ErrorBoundary>
  );
};

SentPosts.propTypes = {
  fetchSentPosts: PropTypes.func.isRequired,
  onConfirmDismissComposer: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  moreToLoad: PropTypes.bool, // eslint-disable-line
  tabId: PropTypes.string,
  profileId: PropTypes.string,
  page: PropTypes.number, // eslint-disable-line
  loadMore: PropTypes.func,
  has30DaySentPostsLimitFeature: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      date: PropTypes.string,
      queueItemType: PropTypes.string,
      dayOfWeek: PropTypes.string,
      hasCommentEnabled: PropTypes.bool,
    })
  ).isRequired,
  total: PropTypes.number,
  showComposer: PropTypes.bool,
  analyzeCrossSale: PropTypes.bool,
  editMode: PropTypes.bool,
  onComposerCreateSuccess: PropTypes.func.isRequired,
  onEditClick: PropTypes.func,
  onShareAgainClick: PropTypes.func,
  onShareLinkClick: PropTypes.func,
  onCampaignTagClick: PropTypes.func,
  isManager: PropTypes.bool,
  isDisconnectedProfile: PropTypes.bool,
  hasFirstCommentFlip: PropTypes.bool,
  hasCampaignsFeature: PropTypes.bool,
  hasAnalyticsOnPosts: PropTypes.bool,
  hasTwitterImpressions: PropTypes.bool,
  profileServiceType: PropTypes.string,
  profileService: PropTypes.string,
};

SentPosts.defaultProps = {
  loading: true,
  moreToLoad: false,
  page: 1,
  total: 0,
  showComposer: false,
  analyzeCrossSale: false,
  editMode: false,
  isManager: true,
  hasFirstCommentFlip: false,
  hasCampaignsFeature: false,
  isDisconnectedProfile: false,
  onEditClick: () => {}, // eslint-disable-line
  onShareAgainClick: () => {}, // eslint-disable-line
  onShareLinkClick: () => {}, // eslint-disable-line
  onCampaignTagClick: () => {}, // eslint-disable-line
  hasAnalyticsOnPosts: false,
  hasTwitterImpressions: false,
  tabId: '',
  profileId: '',
  loadMore: () => {}, // eslint-disable-line
  profileServiceType: '',
  profileService: '',
};

export default SentPosts;
